import { TicketOfficeView } from '../../../types/ticket-office';
import { GridViewProps } from '../../components/GridView';
import { openProductModal, openSerialTicketModal, syncProducts } from './product-utils';
import { formatPrice } from '../../number-utils';
import { ProductGridItem } from '../../../types/types';

export const getGridView = (view: TicketOfficeView): GridViewProps => {
    const { state, update } = view;
    const {
        lang,
        currentGridId,
        productButtonGrids,
        products,
        serialTicketTypes,
        currencySymbol,
        giftCardTypes,
        cardTypes,
    } = state;

    const currentGrid = productButtonGrids.filter((x) => x.id === currentGridId)[0] ?? productButtonGrids[0];

    return {
        buttons: productButtonGrids.map((x) => ({
            label: x.title,
            name: x.title.toLowerCase(),
            current: currentGridId === x.id,
            onClick: () => {
                view.state.currentGridId = x.id;
                view.state.isGridDropdownOpen = false;
                view.update();
            },
        })),
        dropdownLabel: currentGrid.title,
        onDropdownClick: () => {
            state.isGridDropdownOpen = !state.isGridDropdownOpen;
            update();
        },
        isDropdownOpen: state.isGridDropdownOpen,
        grid: {
            title: currentGrid.title,
            name: currentGrid.title.toLowerCase(),
            products: currentGrid.products.map((row, rowIndex) =>
                row.map((gridItem: ProductGridItem | null, colIndex) => {
                    if (!gridItem) {
                        return;
                    }

                    let name = '';
                    let unitPrice = 0;
                    const hidePrice = false;

                    if (gridItem.variantId) {
                        const product = products.find((p) => p.variants.some((v) => v.id === gridItem.variantId));
                        if (!product) {
                            return;
                        }

                        const variant = product.variants.find((v) => v.id === gridItem.variantId);
                        name = `${product.name}` + (variant.name ? ` (${variant.name})` : '');
                        unitPrice = variant.unitPrice;
                    } else if (gridItem.serialTicketTypeId) {
                        const type = serialTicketTypes.find((t) => t.id === gridItem.serialTicketTypeId);
                        if (!type) {
                            return;
                        }

                        name = type.name;
                        unitPrice = type.unitPrice;
                    } else if (gridItem.giftCardTypeId) {
                        // Find the base type (not cart instances which have quantity > 0)
                        const type =
                            giftCardTypes.find((t) => t.id === gridItem.giftCardTypeId && t.quantity === 0) ||
                            giftCardTypes.find((t) => t.id === gridItem.giftCardTypeId);
                        if (!type) {
                            return;
                        }

                        name = type.name;
                        // Only show price for fixed-value gift cards
                        if (!type.allow_custom_value) {
                            unitPrice = type.unitPrice;
                        } else {
                            // For open gift cards, set a special value to hide price
                            unitPrice = -1;
                        }
                    } else if (gridItem.cardTypeId) {
                        const type = cardTypes.find((t) => t.id === gridItem.cardTypeId);
                        if (!type) {
                            return;
                        }

                        name = type.name;
                        unitPrice = type.unitPrice;
                    }

                    return {
                        title: gridItem.title ?? name,
                        price: unitPrice === -1 ? null : formatPrice(unitPrice, currencySymbol),
                        color: gridItem.color,
                        active: gridItem.active,
                        // Pass through the IDs for proper button handling
                        variantId: gridItem.variantId,
                        serialTicketTypeId: gridItem.serialTicketTypeId,
                        giftCardTypeId: gridItem.giftCardTypeId,
                        cardTypeId: gridItem.cardTypeId,
                        isRenewal: gridItem.isRenewal,
                    };
                })
            ),
            onClick: (row, col) => {
                const item = currentGrid.products[row][col];

                if (!item.active) {
                    return;
                }

                const cellVariantId = item.variantId;

                if (cellVariantId) {
                    const product = products.find((p) => p.variants.some((v) => v.id === cellVariantId));
                    const variant = product.variants.find((v) => v.id === cellVariantId);

                    if (variant.maxQuantity === undefined || variant.quantity < variant.maxQuantity) {
                        variant.quantity += 1;
                        syncProducts(view);
                    } else {
                        window.toastr.warning(lang.errors.maxQuantityReached);
                    }
                } else {
                    if (item.serialTicketTypeId) {
                        const type = serialTicketTypes.find((t) => t.id === item.serialTicketTypeId);
                        type.quantity += 1;
                    } else if (item.giftCardTypeId) {
                        // Find the base type (not cart instances which have quantity > 0)
                        const type =
                            giftCardTypes.find((t) => t.id === item.giftCardTypeId && t.quantity === 0) ||
                            giftCardTypes.find((t) => t.id === item.giftCardTypeId);

                        // Check if this is an open gift card
                        if (type.allow_custom_value) {
                            // Create a clean base type for adding new items (not editing)
                            const cleanType = {
                                ...type,
                                quantity: 0,
                                unitPrice: 0,
                            };
                            // Open the custom amount modal
                            state.isOpenGiftCardModalOpen = true;
                            state.openGiftCardModalType = cleanType;
                            update();
                            return; // Don't call syncProducts for modal
                        } else {
                            // Regular fixed amount gift card
                            type.quantity += 1;
                        }
                    } else if (item.cardTypeId) {
                        const type = cardTypes.find((t) => t.id === item.cardTypeId);

                        if (item.isRenewal) {
                            // Open renewal modal
                            state.isMembershipRenewalModalOpen = true;
                            state.membershipRenewalModalCardTypeId = item.cardTypeId;
                            update();
                            return; // Don't call syncProducts for membership modals
                        } else {
                            // Open new membership modal
                            state.isMembershipPurchaseModalOpen = true;
                            state.membershipPurchaseModalCardTypeId = item.cardTypeId;
                            update();
                            return; // Don't call syncProducts for membership modals
                        }
                    }

                    syncProducts(view);
                }
            },
        },
    };
};
